import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IconButton } from "@bloomberg/media.atomics";
import { FullscreenClose, FullscreenOpen } from "@bloomberg/media.icons";
import { ButtonDropdown } from "@bloomberg/media-ui-dropdown";
import type { DropdownOption } from "@bloomberg/media-ui-dropdown/types";
import { createClasses, Tab, Tabs } from "@bloomberg/media-ui-core";
import styles from "./styles.module.css";
import type {
  ChartType,
  DataInterval,
  TickerChartDateRange,
} from "~types/ticker-chart";
import { INTERVAL_LABELS } from "~constants/ticker-chart";
import { getAvailableIntervals } from "~utils/ticker-chart";

function LineChartIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <polyline
        points="1,12 4,8 7,10 10,4 15,6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function CandlestickChartIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      {/* Left candle - filled/bearish */}
      <line x1="3" y1="1" x2="3" y2="3" stroke="currentColor" strokeWidth="1" />
      <rect x="1" y="3" width="4" height="8" fill="currentColor" />
      <line
        x1="3"
        y1="11"
        x2="3"
        y2="15"
        stroke="currentColor"
        strokeWidth="1"
      />
      {/* Middle candle - hollow/bullish, small body */}
      <line x1="8" y1="4" x2="8" y2="7" stroke="currentColor" strokeWidth="1" />
      <rect
        x="6.5"
        y="7"
        width="3"
        height="3"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
      <line
        x1="8"
        y1="10"
        x2="8"
        y2="14"
        stroke="currentColor"
        strokeWidth="1"
      />
      {/* Right candle - hollow/bullish, tall body */}
      <line
        x1="13"
        y1="2"
        x2="13"
        y2="4"
        stroke="currentColor"
        strokeWidth="1"
      />
      <rect
        x="11"
        y="4"
        width="4"
        height="9"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
      <line
        x1="13"
        y1="13"
        x2="13"
        y2="15"
        stroke="currentColor"
        strokeWidth="1"
      />
    </svg>
  );
}

type TickerChartControlsProps = {
  readonly chartType: ChartType;
  readonly interval: DataInterval;
  readonly dateRange: TickerChartDateRange;
  readonly onChartTypeChange: (chartType: ChartType) => void;
  readonly onIntervalChange: (interval: DataInterval) => void;
  readonly isComparisonMode?: boolean;
  readonly showExpandIcon?: boolean;
  readonly isExpanded?: boolean;
  readonly onExpandClick?: () => void;
  readonly expandButtonRef?: React.RefObject<HTMLButtonElement>;
};

export default function TickerChartControls({
  chartType,
  interval,
  dateRange,
  onChartTypeChange,
  onIntervalChange,
  isComparisonMode = false,
  showExpandIcon = false,
  isExpanded = false,
  onExpandClick,
  expandButtonRef,
}: TickerChartControlsProps): React.ReactElement {
  const [selectedChartType, setSelectedChartType] =
    useState<ChartType>(chartType);

  const availableIntervals = useMemo(
    () => getAvailableIntervals(dateRange),
    [dateRange]
  );

  const intervalOptions = useMemo(
    () =>
      availableIntervals.map((int) => ({
        displayValue: INTERVAL_LABELS[int],
        value: int,
      })),
    [availableIntervals]
  );

  const selectedIntervalOption = useMemo(
    () =>
      intervalOptions.find((option) => option.value === interval) ??
      intervalOptions[0],
    [intervalOptions, interval]
  );

  useEffect(() => {
    if (isComparisonMode) {
      setSelectedChartType("line");
      onChartTypeChange("line");
    }
  }, [isComparisonMode, onChartTypeChange]);

  const handleIntervalSelect = useCallback(
    (option?: DropdownOption | null) => {
      if (option?.value) {
        onIntervalChange(option.value as DataInterval);
      }
    },
    [onIntervalChange]
  );

  const handleChartTypeChange = useCallback(
    (_tabTitle: string, tabIndex?: number) => {
      if (tabIndex === 1) {
        setSelectedChartType("candlestick");
        onChartTypeChange("candlestick");
      } else {
        setSelectedChartType("line");
        onChartTypeChange("line");
      }
    },
    [onChartTypeChange]
  );

  return (
    <div className={styles.controls}>
      <Tabs
        className={createClasses([
          styles.chartTypeTabs,
          isComparisonMode && styles.chartTypeDisabled,
        ])}
        size="small"
        variant="gliding"
        onTabSwitch={handleChartTypeChange}
      >
        <Tab
          title=""
          Icon={<LineChartIcon />}
          isActive={selectedChartType === "line"}
          isDefault={selectedChartType === "line"}
        >
          <></>
        </Tab>
        <Tab
          title=""
          Icon={<CandlestickChartIcon />}
          isActive={selectedChartType === "candlestick"}
          isDefault={selectedChartType === "candlestick"}
        >
          <></>
        </Tab>
      </Tabs>

      <ButtonDropdown
        className={styles.intervalDropdown}
        selectedItemOverride={selectedIntervalOption}
        onSelect={handleIntervalSelect}
        options={intervalOptions}
      />
      {showExpandIcon && (
        <IconButton
          ref={expandButtonRef}
          title={isExpanded ? "Minimize chart" : "Expand chart"}
          size="medium"
          variant="ghost"
          onClick={() => onExpandClick?.()}
          className={styles.expandButton}
        >
          {isExpanded ? <FullscreenClose /> : <FullscreenOpen />}
        </IconButton>
      )}
    </div>
  );
}
